import HttpClient from "api";
import {
	ProjectPowerPointTemplateConfiguration,
	STProject,
	STProjectForm,
	STProjectUsersPayload,
	STProjectUsersResponse,
	TProjectChecklistForm,
	TProjectMissingSummariesPayload,
} from "api/projectApi/types";
import type {STEvents} from "api/types";
import {AxiosRequestConfig} from "axios";
import {TPaginationParam} from "../candidateApi/types";

class MapxProjectApi extends HttpClient {
	// `anonymise` is sent only when it is on, so an API without the field still accepts normal exports
	async exportProject(
		projectID: number | string,
		anonymise = false,
		config: AxiosRequestConfig = {responseType: "arraybuffer"},
	) {
		return this.doGet(`/mapx/projects/${projectID}/export`, {
			...config,
			...(anonymise && {params: {anonymise: true}}),
		});
	}

	async exportProjectTrackPeopleReport(
		projectID: number,
		positionChangedDays: number,
		anonymise = false,
		config: AxiosRequestConfig = {responseType: "arraybuffer"},
	) {
		const body = {
			position_change_days: positionChangedDays,
			...(anonymise && {anonymise: true}),
		};

		return this.doPost(`/projects/${projectID}/tracking/export`, body, config);
	}

	async getPresentationAvailableTemplates(config = {}) {
		return this.doGet(`/presentation-available-templates`, config);
	}

	async downloadProjectPowerpoint(
		projectID: number | string,
		payload: ProjectPowerPointTemplateConfiguration,
		config: AxiosRequestConfig = {
			responseType: "blob",
			headers: {
				Accept: "application/octet-stream",
			},
		},
	) {
		return this.doPost(`/projects/${projectID}/export-to-pptx`, payload, config);
	}

	async getProjectMissingSummaries(
		projectID: number | string,
		payload: TProjectMissingSummariesPayload,
		config = {},
	) {
		return this.doPost(`/projects/${projectID}/missing-summaries`, payload, config);
	}

	async deleteProject(projectID: number) {
		return this.doDelete(`/mapx/projects/${projectID}`);
	}

	async getProject(projectID: number, config = {}) {
		return this.doGet<STProject>(`/mapx/projects/${projectID}`, config);
	}

	async getProjects(pagination: TPaginationParam, name = "") {
		let filterQuery = `?page=${pagination.page}&per_page=${pagination.per_page}`;

		// Conditionally add the optional name parameter, encoded: a project called
		// "R&D Lead" or "Finance / Ops" would otherwise cut the query string in half
		// or invent a parameter, and the search would silently answer for something
		// else. Reached for the first time by the Extended Search import picker,
		// which searches on every few keystrokes.
		if (name !== undefined && name !== null && name !== "") {
			filterQuery += `&name=${encodeURIComponent(name)}`;
		}

		return this.doGet(`/mapx/projects${filterQuery}`);
	}

	async saveProject(formData: STProjectForm, config = {}) {
		return this.doPost<STProject, STProjectForm>("/mapx/projects", formData, config);
	}

	async updateProject(projectID: number, formData: STProjectForm, config = {}) {
		return this.doPatch<STProject, STProjectForm>(
			`/mapx/projects/${projectID}`,
			formData,
			config,
		);
	}

	async shareProjectWithUsers(projectID: number, payload: STProjectUsersPayload, config = {}) {
		return this.doPost(`/projects/${projectID}/users`, payload, config);
	}

	async removeUserFromProject(projectID: number, payload: STProjectUsersPayload, config = {}) {
		return this.doDelete(`/projects/${projectID}/users`, payload, config);
	}

	async getProjectUsers(projectID: number, config = {}) {
		return this.doGet<STProjectUsersResponse>(`/projects/${projectID}/users`, config);
	}

	async getProjectSuccessChecklistInfo(projectId: number, config = {}) {
		return this.doGet(`/project/${projectId}/checklist`, config);
	}

	async updateProjectChecklistInfo(
		projectId: number,
		formData: TProjectChecklistForm,
		config = {},
	) {
		return this.doPatch(`/project/${projectId}/checklist`, formData, config);
	}

	async saveEvents(data: STEvents[], config = {}) {
		return this.doPost(`/mapx/events`, data, config);
	}

	async getProjectProgressStatuses(config = {}) {
		return this.doGet(`/progress-statuses`, config);
	}

	async getProjectCandidateStatuses(targetListID: number, config = {}) {
		return this.doGet(`/target-lists/${targetListID}/get-statuses`, config);
	}

	async getProjectAssignments(config = {}) {
		return this.doGet(`/integrations/invenias/assignments`, config);
	}

	async connectProjectAssignment(projectId: number, assignmentId: string, config = {}) {
		return this.doPost(
			`/projects/${projectId}/integrations`,
			{assignment_id: assignmentId},
			config,
		);
	}

	async syncProjectAssignment(projectId: number, config = {}) {
		return this.doPost(`/projects/${projectId}/sync-statuses`, {}, config);
	}
}

const mapxProjectApi = new MapxProjectApi();

export default mapxProjectApi;
